import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, CircleDollarSign, HandCoins, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

function playFlip() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "triangle"; o.frequency.setValueAtTime(520, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.32);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch { /* audio blocked */ }
}

function playBust() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "square"; o.frequency.setValueAtTime(300, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(90, ctx.currentTime + 0.25);
    g.gain.setValueAtTime(0.16, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.32);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch { /* audio blocked */ }
}

const FACE = {
  heads: { grad: "radial-gradient(circle at 38% 30%, #fef08a, #E0B84C 55%, #92600e)", ring: "234,179,8", letter: "H", label: "Heads" },
  tails: { grad: "radial-gradient(circle at 38% 30%, #e2e8f0, #94a3b8 55%, #475569)", ring: "148,163,184", letter: "T", label: "Tails" },
};

function Coin({ side, spinning }) {
  const f = FACE[side] || FACE.heads;
  return (
    <div className={`relative mx-auto h-36 w-36 sm:h-40 sm:w-40 rounded-full grid place-items-center select-none ${spinning ? "animate-flip" : ""}`}
      style={{ background: f.grad, boxShadow: `0 12px 34px rgba(0,0,0,0.55), inset 0 3px 8px rgba(255,255,255,0.5), inset 0 -6px 12px rgba(0,0,0,0.35), 0 0 26px rgba(${f.ring},0.5)`, border: "3px solid rgba(255,255,255,0.35)" }}
      data-testid="coinflip-coin">
      <span className="font-display font-black text-6xl sm:text-7xl" style={{ color: "rgba(0,0,0,0.65)", textShadow: "0 1px 1px rgba(255,255,255,0.4)" }}>{f.letter}</span>
    </div>
  );
}

export default function CoinFlip() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [round, setRound] = useState(null);       // {roundId, multiplier, nextMultiplier}
  const [busy, setBusy] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [face, setFace] = useState("heads");       // currently displayed coin face
  const [status, setStatus] = useState(null);      // null | "lost" | "won"
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);
  const streak = useRef(0);
  const stakeRef = useRef(0);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="coinflip-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="coinflip-go-home">Go Home</Button></Link>
    </div>
  );

  const active = round && status === null;
  const potential = Math.round(stakeRef.current * (round?.multiplier || 1));

  const start = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio(); setBusy(true); setStatus(null);
    try {
      const res = await gamesApi.coinflipStart({ stake: s });
      stakeRef.current = s;
      setRound(res);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setBusy(false); }
  };

  const guess = async (choice) => {
    if (!active || busy) return;
    setBusy(true); setSpinning(true); playFlip();
    try {
      const res = await gamesApi.coinflipFlip({ roundId: round.roundId, choice });
      await new Promise((ok) => setTimeout(ok, 550));
      setFace(res.result);
      if (res.win) {
        setRound((r) => ({ ...r, multiplier: res.multiplier, nextMultiplier: res.nextMultiplier }));
      } else {
        setStatus("lost");
        streak.current = 0;
        setRecent((p) => [{ m: 0, won: false }, ...p].slice(0, 14));
        playBust();
      }
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setSpinning(false); setBusy(false); }
  };

  const cashout = async () => {
    if (!active || busy || (round?.multiplier || 1) <= 1) return;
    setBusy(true);
    try {
      const res = await gamesApi.coinflipCashout({ roundId: round.roundId });
      setStatus("won");
      streak.current += 1;
      setRecent((p) => [{ m: res.multiplier, won: true }, ...p].slice(0, 14));
      playCoinSplash();
      setReveal({ label: `${Number(res.multiplier).toFixed(2)}x`, won: true, payout: res.payout, streak: streak.current });
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 pb-28" data-testid="coinflip-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2"><CircleDollarSign className="h-6 w-6 text-primary" /> Coin Flip</h1>
          <p className="text-sm text-muted-foreground">Call it right · compound 1.5x each flip · cash out anytime</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="coinflip-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Table */}
      <div className="relative rounded-2xl p-6 overflow-hidden"
        style={{ background: "radial-gradient(ellipse 120% 90% at 50% 0%, #10402c, #062017 70%)", border: "1px solid rgba(217,181,69,0.45)", boxShadow: "inset 0 0 60px rgba(0,0,0,0.5)" }}>
        <div className="relative flex items-center justify-between mb-4">
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Streak</span>
            <span className="font-display font-black text-lg text-yellow-300" data-testid="coinflip-multiplier">{Number(round?.multiplier || 1).toFixed(2)}x</span>
          </div>
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10 text-right">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Cash value</span>
            <span className="font-display font-black text-lg text-primary" data-testid="coinflip-potential">{round ? inr(potential) : "—"}</span>
          </div>
        </div>

        <div className="relative py-2"><Coin side={face} spinning={spinning} /></div>

        <div className="mt-4 h-6 text-center text-sm">
          {status === "lost" && <span className="font-semibold text-rose-400" data-testid="coinflip-status-lost">Wrong call — landed {FACE[face].label}. Streak busted.</span>}
          {status === "won" && <span className="font-semibold text-yellow-300" data-testid="coinflip-status-won">Cashed out {Number(round?.multiplier || 0).toFixed(2)}x!</span>}
          {active && <span className="text-emerald-100/70">Heads or tails? Each correct call pays 1.5x.</span>}
          {!round && <span className="text-muted-foreground">Set your stake and flip</span>}
        </div>

        {active && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button onClick={() => guess("heads")} disabled={busy} data-testid="coinflip-heads-btn"
              className="rounded-xl py-3 font-extrabold text-black transition-[filter,transform] hover:brightness-110 active:scale-95 disabled:opacity-50 flex flex-col items-center"
              style={{ background: "linear-gradient(145deg,#fef08a,#E0B84C 55%,#b45309)", boxShadow: "0 6px 16px rgba(224,184,76,0.35), inset 0 1px 0 rgba(255,255,255,0.5)" }}>
              <span>Heads</span>
              <span className="text-[11px] font-bold opacity-80">{Number(round.nextMultiplier || 1.5).toFixed(2)}x</span>
            </button>
            <button onClick={() => guess("tails")} disabled={busy} data-testid="coinflip-tails-btn"
              className="rounded-xl py-3 font-extrabold text-white transition-[filter,transform] hover:brightness-110 active:scale-95 disabled:opacity-50 flex flex-col items-center"
              style={{ background: "linear-gradient(145deg,#cbd5e1,#94a3b8 55%,#475569)", boxShadow: "0 6px 16px rgba(148,163,184,0.35), inset 0 1px 0 rgba(255,255,255,0.4)" }}>
              <span>Tails</span>
              <span className="text-[11px] font-bold opacity-90">{Number(round.nextMultiplier || 1.5).toFixed(2)}x</span>
            </button>
          </div>
        )}

        {active && (
          <Button className="mt-3 w-full h-11 font-extrabold" onClick={cashout} disabled={busy || (round?.multiplier || 1) <= 1} data-testid="coinflip-cashout-btn">
            <HandCoins className="h-5 w-5 mr-1.5" />
            {(round?.multiplier || 1) <= 1 ? "Win a flip to enable cash out" : `Cash Out ${inr(potential)}`}
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="coinflip-controls">
        <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
        <Input type="number" min={20} max={100000} value={stake} disabled={active}
          onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="coinflip-stake-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
            const special = q === 10000;
            return (
              <button key={q} onClick={() => !active && setStake(String(q))} disabled={active} data-testid={`coinflip-chip-${q}`}
                className={special
                  ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]"
                  : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                +{q}
              </button>
            );
          })}
        </div>
        <Button className="mt-5 w-full h-12 font-extrabold text-base" onClick={start} disabled={busy || active} data-testid="coinflip-flip-btn">
          <CircleDollarSign className="h-5 w-5 mr-1.5" />
          {active ? "Round in progress…" : busy ? "Dealing…" : "Start Flip"}
        </Button>
        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="coinflip-recent">
            <RotateCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {recent.map((r, i) => (
              <span key={i} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${r.won ? "bg-primary/15 text-yellow-300" : "bg-secondary text-muted-foreground"}`}>{Number(r.m).toFixed(2)}x</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
